import { cn } from '@endure/ui';

import forgeLogoSrc from 'assets/img/forgeLogo.svg';
import forgeLogoWithTextSrc from 'assets/img/forgeLogoWithText.svg';
import { Delimiter, Icon } from 'components';
import { PAGE_CONTAINER_ID } from 'constants/layout';
import { routes } from 'constants/routing';
import { Link } from 'containers/Link';
import { useTranslation } from 'libs/translations';
import { motion, useReducedMotion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { store } from '../store';
import { ChainSelect } from './ChainSelect';
import { ClaimRewardsButton } from './ClaimRewardsButton';
import { ConnectButton } from './ConnectButton';
import { MenuItem } from './MenuItem';
import { NavButtonWrapper } from './NavButtonWrapper';
import { Settings } from './Settings';
import { SettingsButton } from './SettingsButton';
import { useMenuItems } from './useMenuItems';

export interface NavBarProps
  extends Omit<
    React.HTMLAttributes<HTMLDivElement>,
    'onDrag' | 'onDragStart' | 'onDragEnd' | 'onAnimationStart'
  > {
  variant?: 'dark' | 'light';
}

const BAR_VARIANTS = {
  top: { y: 0 },
  hidden: { y: '-110%' },
  revealed: { y: 0 },
};

export const NAV_BAR_HEIGHT_PX = 80;
export const NAV_BAR_TRANSITION = { type: 'spring', stiffness: 380, damping: 34 } as const;

export const NavBar: React.FC<NavBarProps> = ({
  className,
  variant = 'dark',
  ...containerProps
}) => {
  const { t } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
  const isLight = variant === 'light';
  const navRevealState = store.use.navRevealState();
  const shouldReduceMotion = useReducedMotion();

  // Jumping straight back to the top from the hidden state (scroll-to-top)
  // must not glide transparent bar contents over the page — snap instead.
  const previousNavRevealStateRef = useRef(navRevealState);
  const shouldSnap = previousNavRevealStateRef.current === 'hidden' && navRevealState === 'top';

  useEffect(() => {
    previousNavRevealStateRef.current = navRevealState;
  }, [navRevealState]);

  const setMobileMenu = ({ isOpen }: { isOpen: boolean }) => {
    // Toggle scroll on page container and body tags
    const pageContainerDom = document.getElementById(PAGE_CONTAINER_ID);

    if (isOpen) {
      pageContainerDom?.classList.add('overflow-hidden');
      document.body.classList.add('overflow-hidden');
    } else {
      pageContainerDom?.classList.remove('overflow-hidden');
      document.body.classList.remove('overflow-hidden');
    }

    setIsMobileMenuOpen(isOpen);
  };

  // Release the scroll lock if the nav unmounts while the menu is open
  useEffect(
    () => () => {
      document.getElementById(PAGE_CONTAINER_ID)?.classList.remove('overflow-hidden');
      document.body.classList.remove('overflow-hidden');
    },
    [],
  );

  const closeMobileMenu = () => setMobileMenu({ isOpen: false });
  const toggleMobileMenu = () => setMobileMenu({ isOpen: !isMobileMenuOpen });

  const menuItems = useMenuItems();

  return (
    <nav className="absolute inset-x-0 top-0 z-50">
      {/* The bar alone animates: the mobile menu panel below is position:fixed
          and must never live inside a transformed ancestor. */}
      <motion.div
        variants={BAR_VARIANTS}
        animate={navRevealState}
        initial={false}
        transition={shouldReduceMotion || shouldSnap ? { duration: 0 } : NAV_BAR_TRANSITION}
        className={cn(
          'h-20 pr-5 flex items-center justify-between',
          `forge-${variant}-nav`,
          navRevealState === 'top' ? 'forge-nav--flat' : 'forge-nav--overlay',
          className,
        )}
        {...containerProps}
      >
        <div className="flex items-center gap-x-1">
          <Link
            className="flex h-full flex-none items-center justify-center px-5 py-6"
            to={routes.landing.path}
            onClick={closeMobileMenu}
          >
            <img
              src={forgeLogoSrc}
              alt={t('layout.menu.forgeLogoAlt')}
              className={cn('forge-nav-logo h-8 sm:hidden', isLight && 'invert')}
            />

            <img
              src={forgeLogoWithTextSrc}
              alt={t('layout.menu.forgeLogoAlt')}
              className={cn('forge-nav-logo h-8 hidden sm:block', isLight && 'invert')}
            />
          </Link>

          {/* XL and up menu. Remounted when the bar hides so an open dropdown
              can never be left orphaned mid-transition. */}
          <div
            className="hidden items-center gap-x-3 xl:flex"
            key={navRevealState === 'hidden' ? 'nav-menu-hidden' : 'nav-menu-visible'}
          >
            {menuItems.map((item) => (
              <MenuItem key={item.label} item={item} onClick={closeMobileMenu} variant={variant} />
            ))}
          </div>
        </div>

        <div className="flex items-center gap-x-2 h-9 sm:h-12">
          <ClaimRewardsButton className="h-full hidden sm:flex" />

          <ChainSelect
            menuClassName="forge-nav-select-menu"
            dropdownClassName="forge-nav-select-options"
            optionClassName="forge-nav-select-option px-4 h-12"
            buttonClassName="forge-nav-control h-9 px-3 py-0 sm:h-12"
          />

          <ConnectButton
            className={cn(
              'h-full max-w-xs flex-1 px-3 whitespace-nowrap',
              'forge-nav-control forge-nav-connect',
            )}
          />

          <NavButtonWrapper
            aria-label={t('layout.menu.label')}
            className={cn(
              'size-9 px-0 sm:size-12 xl:hidden',
              'forge-nav-control forge-nav-icon-button',
            )}
            onClick={toggleMobileMenu}
          >
            <Icon
              name={isMobileMenuOpen ? 'closeRounded' : 'burger'}
              className={cn('text-foreground', isMobileMenuOpen && 'size-3')}
            />
          </NavButtonWrapper>

          <SettingsButton
            className={cn('h-full px-0 hidden xl:flex', 'forge-nav-control forge-nav-icon-button')}
            iconClassName="text-muted"
          />
        </div>
      </motion.div>

      {/* Mobile/tablet menu */}
      <div
        className={cn(
          isMobileMenuOpen ? 'fixed' : 'hidden',
          'bottom-0 left-0 right-0 z-40 p-5 overflow-y-auto xl:hidden',
          `top-20 forge-${variant}-mobile-menu bg-elevated text-foreground`,
        )}
      >
        <div className="flex items-center justify-between mb-5">
          <p>{t('layout.menu.label')}</p>

          <ClaimRewardsButton className="px-4" />
        </div>

        <div className="mb-2">
          {menuItems.map((item) => (
            <MenuItem key={item.label} item={item} onClick={closeMobileMenu} variant={variant} />
          ))}
        </div>

        <Delimiter className="mb-6" />

        <Settings />
      </div>
    </nav>
  );
};
